import { useState, useEffect, type Key } from "react";
import { Filter, X, ChevronDown, MapPin, Tag } from "lucide-react";
import { motion } from "framer-motion";
import Select from "../../../../shared/components/form/Select";
import projectTypeService, {
  ProjectTypeDropdownOption,
} from "../../../../services/projectTypeService";
import locationService from "../../../../services/locationService";

const BRAND = {
  primary: "#339274",
  primaryHover: "#29795f",
  primaryLight: "#F8FCFA",
  neutral: "#6B7280",
  border: "#E5E7EB",
};

type OptionType = { label: string; value: string };

const STATUS_PILLS: OptionType[] = [
  { value: "", label: "All" },
  { value: "started", label: "Project started" },
  { value: "rejected", label: "Rejected" },
  { value: "pending_approval", label: "Waiting for approval" },
  { value: "ready", label: "Ready to send for review" },
  { value: "draft", label: "Draft" },
];

interface ProjectFilterBarProps {
  projectTypeId: string;
  setProjectTypeId: (val: string) => void;
  location: string;
  setLocation: (val: string) => void;
  approvalStatus: string;
  setApprovalStatus: (val: string) => void;
  setPage: (val: number) => void;
  hideStatus?: boolean;
}

const ProjectFilterBar = ({
  projectTypeId,
  setProjectTypeId,
  location,
  setLocation,
  approvalStatus,
  setApprovalStatus,
  setPage,
  hideStatus = false,
}: ProjectFilterBarProps) => {
  const [projectTypeOptions, setProjectTypeOptions] = useState<OptionType[]>([
    { value: "", label: "All types" },
  ]);
  const [loadingProjectTypes, setLoadingProjectTypes] = useState(false);
  const [locationOptions, setLocationOptions] = useState<OptionType[]>([
    { value: "", label: "All locations" },
  ]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  useEffect(() => {
    loadProjectTypes();
    loadLocations();
  }, []);

  const loadProjectTypes = async () => {
    setLoadingProjectTypes(true);
    try {
      const projectTypes = await projectTypeService.getProjectTypesForDropdown();
      const options: OptionType[] = [
        { value: "", label: "All types" },
        ...projectTypes.map((pt: ProjectTypeDropdownOption) => ({
          value: pt.value,
          label: pt.label,
        })),
      ];
      setProjectTypeOptions(options);
    } catch (error) {
      console.error("Failed to load project types:", error);
    } finally {
      setLoadingProjectTypes(false);
    }
  };

  const loadLocations = async () => {
    setLoadingLocations(true);
    try {
      const locations = await locationService.getLocationsForDropdown();
      const options: OptionType[] = [
        { value: "", label: "All locations" },
        ...locations.map((loc: { _id: string; id?: Key; name: string }) => ({
          value: String(loc._id ?? loc.id ?? ""),
          label: loc.name,
        })),
      ];
      setLocationOptions(options);
    } catch (error) {
      console.error("Failed to load locations:", error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleResetFilters = () => {
    setProjectTypeId("");
    setLocation("");
    setApprovalStatus("");
    setPage(1);
    setFiltersExpanded(false);
  };

  // Location is the only filter inside the collapsible "Filters" panel now —
  // project type lives in the dynamic tab row above.
  const hasActiveFilters = Boolean(location || approvalStatus || projectTypeId);
  const hasLocationFilter = Boolean(location);

  const handleStatusPill = (value: string) => {
    setApprovalStatus(value);
    setPage(1);
  };

  const handleProjectTypeTab = (value: string) => {
    setProjectTypeId(value);
    setPage(1);
  };

  return (
    <div className="font-[family-name:var(--font-outfit)] space-y-3">
      {/* Project type tabs — dynamic from master, underline-style with animated indicator */}
      <div className="border-b border-gray-200">
        <div
          className="flex items-center gap-1 overflow-x-auto -mb-px"
          role="tablist"
          aria-label="Filter projects by type"
        >
          {projectTypeOptions.map((opt) => {
            const active = projectTypeId === opt.value;
            return (
              <button
                key={opt.value || "all-types"}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => handleProjectTypeTab(opt.value)}
                disabled={loadingProjectTypes}
                className={`relative shrink-0 px-4 py-2.5 text-[13px] font-semibold tracking-tight transition-colors whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#339274]/40 rounded-t-md ${
                  active
                    ? "text-[#29795f]"
                    : "text-gray-500 hover:text-gray-800 hover:bg-gray-50/80"
                } ${loadingProjectTypes ? "opacity-60" : ""}`}
              >
                <span className="relative z-10">{opt.label}</span>
                {active && (
                  <motion.span
                    layoutId="project-type-tab-indicator"
                    className="absolute left-3 right-3 -bottom-px h-[2.5px] rounded-full"
                    style={{
                      background: `linear-gradient(90deg, ${BRAND.primary}, ${BRAND.primaryHover})`,
                    }}
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Status pills — always visible (unless hidden, e.g. AMS lists), one-tap filter */}
      {!hideStatus && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mr-1 flex items-center gap-1">
            <Tag className="w-3.5 h-3.5" />
            Status
          </span>
          {STATUS_PILLS.map((opt) => (
            <button
              key={opt.value || "all"}
              type="button"
              onClick={() => handleStatusPill(opt.value)}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all ${
                approvalStatus === opt.value
                  ? "bg-[#339274] text-white shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Location filter (collapsible) + reset */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setFiltersExpanded((e) => !e)}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
            hasLocationFilter
              ? "border-[#339274] bg-[#F8FCFA] text-[#29795f]"
              : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
          }`}
        >
          <Filter className="w-4 h-4" />
          Location
          {hasLocationFilter && (
            <span className="min-w-[18px] h-[18px] rounded-full bg-[#339274] text-white text-[10px] font-bold flex items-center justify-center">
              1
            </span>
          )}
          <ChevronDown
            className={`w-4 h-4 transition-transform ${filtersExpanded ? "rotate-180" : ""}`}
          />
        </button>

        {filtersExpanded && (
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <div className="flex items-center gap-1.5 min-w-[160px] max-w-[220px]">
              <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <Select
                options={locationOptions}
                value={locationOptions.find((o) => o.value === location)}
                placeholder={loadingLocations ? "Loading…" : "Location"}
                onChange={(selected: OptionType | null) => {
                  setLocation(selected?.value ?? "");
                  setPage(1);
                }}
                isDisabled={loadingLocations}
                className="text-sm flex-1"
              />
            </div>
          </div>
        )}

        {hasActiveFilters && (
          <button
            type="button"
            onClick={handleResetFilters}
            className="inline-flex items-center gap-1.5 px-2.5 py-2 text-sm font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Clear all filters"
          >
            <X className="w-4 h-4" />
            Clear all
          </button>
        )}
      </div>
    </div>
  );
};

export default ProjectFilterBar;
